import { createRoot } from "react-dom/client"
import type { WorkflowBlockDefinition, WorkflowGraph } from "@/types/leadflow"
import { blockByType } from "@/workflows/blocks/registry"
import { normalizeWorkflowGraph } from "@/workflows/graph"
import { durationDisplayFromMinutes, normalizeConditionConfig, operatorRequiresValue, workflowOperatorByKey, workflowVariableByKey } from "@/workflows/variables/registry"
import { ClassicPreset, NodeEditor } from "rete"
import { AreaPlugin, AreaExtensions } from "rete-area-plugin"
import { ConnectionPlugin, Presets as ConnectionPresets } from "rete-connection-plugin"
import { HistoryExtensions, HistoryPlugin, Presets as HistoryPresets } from "rete-history-plugin"
import { Presets as ReactPresets, ReactPlugin } from "rete-react-plugin"
import { AlarmClock, Bell, CalendarCheck2, CalendarX2, CircleDashed, CircleStop, Clock3, GitBranch, GitFork, KanbanSquare, ListTodo, Mail, MessageSquareText, PencilLine, Plus, RefreshCcw, SquareMousePointer, SplitSquareVertical, Tag, Tags, UserRound, Webhook, type LucideIcon } from "lucide-react"

const WORKFLOW_SOCKET = new ClassicPreset.Socket("workflow")

function workflowToneClass(category?: string) {
  switch (category) {
    case "trigger": return "workflow-tone--trigger workflow-node--trigger"
    case "logic": return "workflow-tone--logic workflow-node--logic"
    case "action": return "workflow-tone--action workflow-node--action"
    default: return "workflow-tone--trigger workflow-node--trigger"
  }
}

type WorkflowNodeData = ClassicPreset.Node<any, any> & {
  blockType: string
  config: Record<string, unknown>
  selected?: boolean
}

export type WorkflowEditorSelection =
  | { kind: "node"; id: string }
  | { kind: "connection"; id: string }
  | null

export type WorkflowEditorCallbacks = {
  onChange?: (graph: WorkflowGraph) => void
  onSelectionChange?: (selection: WorkflowEditorSelection) => void
}

export type WorkflowEditorHandle = {
  destroy(): void
  serialize(): WorkflowGraph
  load(graph: WorkflowGraph): Promise<void>
  addBlock(type: string): Promise<string | null>
  duplicateNode(nodeId: string): Promise<string | null>
  selectNode(nodeId: string): Promise<void>
  deleteNode(nodeId: string): Promise<void>
  deleteConnection(connectionId: string): Promise<void>
  updateNodeConfig(nodeId: string, config: Record<string, unknown>): Promise<void>
  undo(): Promise<void>
  redo(): Promise<void>
}

export async function createWorkflowEditor(container: HTMLDivElement, callbacks: WorkflowEditorCallbacks = {}, initialGraph: WorkflowGraph = { schema_version: 1, nodes: [], connections: [] }) {
  const editor = new NodeEditor<any>()
  const area = new AreaPlugin<any>(container)
  const connection = new ConnectionPlugin<any>()
  const react = new ReactPlugin<any>({ createRoot })
  const history = new HistoryPlugin<any>()
  const selectable = AreaExtensions.selector<any>()
  const selectableNodes = AreaExtensions.selectableNodes(area as any, selectable as any, { accumulating: AreaExtensions.accumulateOnCtrl() } as any)
  let suppressChange = false
  let selectedNodeId: string | null = null

  ;(editor as any).use(area)
  ;(area as any).use(connection)
  ;(area as any).use(react)
  ;(area as any).use(history)
  AreaExtensions.restrictor(area as any, { scaling: { min: 0.4, max: 1.75 } })
  connection.addPreset(ConnectionPresets.classic.setup() as any)
  react.addPreset(ReactPresets.classic.setup({
    customize: {
      node: () => LeadFlowNode,
      socket: () => LeadFlowSocket,
    },
  }) as any)
  history.addPreset(HistoryPresets.classic.setup() as any)
  HistoryExtensions.keyboard(history as any)

  ;(editor as any).addPipe((context: any) => {
    if (suppressChange) return context
    if (context.type === "nodecreated" || context.type === "noderemoved" || context.type === "connectioncreated" || context.type === "connectionremoved" || context.type === "cleared") {
      queueChange()
    }
    return context
  })

  ;(area as any).addPipe((context: any) => {
    if (context.type === "nodepicked") {
      selectedNodeId = context.data.id
      callbacks.onSelectionChange?.({ kind: "node", id: context.data.id })
    }
    if (context.type === "nodetranslated" || context.type === "noderesized") {
      queueChange()
    }
    return context
  })

  const handle: WorkflowEditorHandle = {
    destroy() {
      history.clear()
      area.destroy()
      void editor.clear()
      callbacks.onSelectionChange?.(null)
    },
    serialize() {
      return serializeGraph()
    },
    async load(graph) {
      suppressChange = true
      selectedNodeId = null
      callbacks.onSelectionChange?.(null)
      await editor.clear()
      const normalized = normalizeWorkflowGraph(graph)
      const nodes = new Map<string, WorkflowNodeData>()

      for (const node of normalized.nodes) {
        const block = blockByType(node.type)
        if (!block) continue
        const workflowNode = createNode(block, node.id, node.config)
        nodes.set(node.id, workflowNode)
        await editor.addNode(workflowNode as any)
        await area.translate(workflowNode.id, { x: node.position.x, y: node.position.y })
      }

      for (const connectionItem of normalized.connections) {
        const source = nodes.get(connectionItem.sourceNodeId)
        const target = nodes.get(connectionItem.targetNodeId)
        if (!source || !target) continue
        if (!source.outputs[connectionItem.sourcePort] || !target.inputs[connectionItem.targetPort]) continue
        await editor.addConnection(new ClassicPreset.Connection(source as any, connectionItem.sourcePort as any, target as any, connectionItem.targetPort as any) as any)
      }

      suppressChange = false
      queueChange()
    },
    async addBlock(type) {
      const block = blockByType(type)
      if (!block) return null
      const node = createNode(block)
      await editor.addNode(node as any)
      await area.translate(node.id, positionForNewNode())
      await selectNode(node.id)
      queueChange()
      return node.id
    },
    async duplicateNode(nodeId) {
      const existing = editor.getNode(nodeId) as WorkflowNodeData | undefined
      if (!existing) return null
      const block = blockByType(existing.blockType)
      if (!block) return null
      const duplicate = createNode(block, undefined, cloneConfig(existing.config))
      await editor.addNode(duplicate as any)
      const position = area.nodeViews.get(nodeId)?.position ?? { x: 180, y: 180 }
      await area.translate(duplicate.id, { x: position.x + 140, y: position.y + 96 })
      await selectNode(duplicate.id)
      queueChange()
      return duplicate.id
    },
    async deleteNode(nodeId) {
      if (!editor.getNode(nodeId)) return
      const attachedConnections = editor.getConnections().filter((connection: any) => connection.source === nodeId || connection.target === nodeId)
      for (const connection of attachedConnections) {
        await editor.removeConnection(connection.id)
      }
      await editor.removeNode(nodeId)
      if (selectedNodeId === nodeId) {
        selectedNodeId = null
        callbacks.onSelectionChange?.(null)
      }
      queueChange()
    },
    async deleteConnection(connectionId) {
      if (!editor.getConnection(connectionId)) return
      await editor.removeConnection(connectionId)
      queueChange()
    },
    async updateNodeConfig(nodeId, config) {
      const node = editor.getNode(nodeId) as WorkflowNodeData | undefined
      if (!node) return
      node.config = { ...cloneConfig(node.config), ...cloneConfig(config) }
      await area.update("node", nodeId)
      queueChange()
    },
    async selectNode(nodeId) {
      await selectNode(nodeId)
    },
    async undo() {
      await history.undo()
      queueChange()
    },
    async redo() {
      await history.redo()
      queueChange()
    },
  }

  await handle.load(initialGraph)
  return handle

  async function selectNode(nodeId: string) {
    if (!editor.getNode(nodeId)) return
    selectedNodeId = nodeId
    callbacks.onSelectionChange?.({ kind: "node", id: nodeId })
    await selectableNodes.select(nodeId, false)
  }

  function queueChange() {
    if (suppressChange) return
    queueMicrotask(() => {
      if (!suppressChange) callbacks.onChange?.(serializeGraph())
    })
  }

  function serializeGraph(): WorkflowGraph {
    const nodeIds = new Set(editor.getNodes().map((node: any) => node.id))
    const nodes = editor.getNodes().map((node: any) => {
      const viewPosition = area.nodeViews.get(node.id)?.position
      return {
        id: node.id,
        type: node.blockType,
        position: viewPosition ? { x: viewPosition.x, y: viewPosition.y } : { x: 0, y: 0 },
        config: cloneConfig(node.config),
      }
    })

    const connections = editor.getConnections()
      .filter((connection: any) => nodeIds.has(connection.source) && nodeIds.has(connection.target))
      .map((connection: any) => ({
        id: connection.id,
        sourceNodeId: connection.source,
        sourcePort: String(connection.sourceOutput),
        targetNodeId: connection.target,
        targetPort: String(connection.targetInput),
      }))

    return normalizeWorkflowGraph({ schema_version: 1, nodes, connections })
  }

  function positionForNewNode() {
    const selectedNode = selectedNodeId ? area.nodeViews.get(selectedNodeId) : null
    if (selectedNode?.position) {
      return { x: selectedNode.position.x + 72, y: selectedNode.position.y + 72 }
    }

    const rect = container.getBoundingClientRect()
    const offset = editor.getNodes().length * 52
    return {
      x: Math.max(120, rect.width / 2 - 120 + offset),
      y: Math.max(120, rect.height / 2 - 60 + offset),
    }
  }
}

function createNode(block: WorkflowBlockDefinition, id?: string, config: Record<string, unknown> = {}) {
  const node = new WorkflowNode(block, id, config)
  return node as WorkflowNodeData
}

function cloneConfig(config: Record<string, unknown>) {
  return JSON.parse(JSON.stringify(config ?? {})) as Record<string, unknown>
}

class WorkflowNode extends ClassicPreset.Node<any, any> {
  blockType: string
  config: Record<string, unknown>

  constructor(block: WorkflowBlockDefinition, id?: string, config: Record<string, unknown> = {}) {
    super(block.display_name)
    if (id) this.id = id
    this.blockType = block.type
    this.config = { ...cloneConfig(block.default_config), ...cloneConfig(config) }

    block.inputs.forEach((port, index) => {
      const input = new ClassicPreset.Input(WORKFLOW_SOCKET, port.label, port.multiple)
      input.index = index
      this.addInput(port.key, input)
    })

    block.outputs.forEach((port, index) => {
      const output = new ClassicPreset.Output(WORKFLOW_SOCKET, port.label, port.multiple)
      output.index = index
      this.addOutput(port.key, output)
    })
  }
}

function LeadFlowSocket() {
  return null
}

function LeadFlowNode({ data, emit }: { data: any; emit: any }) {
  const workflowData = data as WorkflowNodeData
  const block = blockByType(workflowData.blockType)
  const Icon = iconForBlock(block?.icon)
  const summary = summarizeBlock(block, workflowData.config)
  const runtimeTone = block?.runtime_available ? "accent" : "signal"
  const selected = Boolean(workflowData.selected)

  return (
    <div className={["workflow-node", selected ? "workflow-node--selected" : "", workflowToneClass(block?.category), block?.draft_only ? "workflow-node--draft-only" : ""].filter(Boolean).join(" ")}>
      <div className="workflow-node__header">
        <div className="workflow-node__icon">{Icon ? <Icon className="h-4 w-4" /> : null}</div>
        <div className="min-w-0 flex-1">
          <div className="workflow-node__title">{block?.display_name ?? workflowData.blockType}</div>
          <div className="workflow-node__summary">{summary}</div>
        </div>
        <span className={runtimeTone === "accent" ? "workflow-node__badge workflow-node__badge--accent" : "workflow-node__badge workflow-node__badge--signal"}>
          {block?.draft_only ? "Reserved" : block?.category ?? "Node"}
        </span>
      </div>

      <div className="workflow-node__meta">
        <span>{block?.description}</span>
        {!block?.runtime_available && <span className="workflow-node__badge workflow-node__badge--signal">Runtime later</span>}
      </div>

      {Object.keys(data.inputs ?? {}).length > 0 && (
        <div className="workflow-node__ports workflow-node__ports--inputs">
          {Object.entries(workflowData.inputs ?? {}).map(([key, input]: any) => (
            <div key={key} className="workflow-node__port workflow-node__port--input">
              <ReactPresets.classic.RefSocket name="workflow-socket" emit={emit} nodeId={workflowData.id} side="input" socketKey={key} payload={input.socket} />
              <span>{input.label || key}</span>
            </div>
          ))}
        </div>
      )}

      {Object.keys(workflowData.outputs ?? {}).length > 0 && (
        <div className="workflow-node__ports workflow-node__ports--outputs">
          {Object.entries(workflowData.outputs ?? {}).map(([key, output]: any) => (
            <div key={key} className="workflow-node__port workflow-node__port--output">
              <span>{output.label || key}</span>
              <ReactPresets.classic.RefSocket name="workflow-socket" emit={emit} nodeId={workflowData.id} side="output" socketKey={key} payload={output.socket} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function iconForBlock(icon?: string): LucideIcon | null {
  switch (icon) {
    case "Plus": return Plus
    case "RefreshCcw": return RefreshCcw
    case "GitBranch": return GitBranch
    case "CalendarCheck2": return CalendarCheck2
    case "CalendarX2": return CalendarX2
    case "SquareMousePointer": return SquareMousePointer
    case "Webhook": return Webhook
    case "Bell": return Bell
    case "Clock3": return Clock3
    case "CircleDashed": return CircleDashed
    case "GitFork": return GitFork
    case "Hourglass": return AlarmClock
    case "AlarmClock": return AlarmClock
    case "SplitSquareVertical": return SplitSquareVertical
    case "CircleStop": return CircleStop
    case "Mail": return Mail
    case "MessageSquareText": return MessageSquareText
    case "UserRound": return UserRound
    case "PencilLine": return PencilLine
    case "Tags": return Tags
    case "Tag": return Tag
    case "ListTodo": return ListTodo
    case "KanbanSquare": return KanbanSquare
    default: return null
  }
}

function humanizeKey(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function formatDuration(value: unknown) {
  const display = durationDisplayFromMinutes(value)
  const unitLabel = display.unit === "days" ? "day" : display.unit === "hours" ? "hour" : "minute"
  return `${display.amount} ${unitLabel}${display.amount === 1 ? "" : "s"}`
}

function conditionSummary(config: Record<string, unknown>) {
  const values = normalizeConditionConfig(config ?? {}, [])
  const variable = workflowVariableByKey(String(values.field ?? ""))
  const operator = workflowOperatorByKey(String(values.operator ?? ""))
  if (!variable || !operator) return "Condition"
  if (!operatorRequiresValue(operator.key)) return `${variable.label} ${operator.label.toLowerCase()}`
  const value = variable.key === "lead.stage_key" ? humanizeKey(String(values.value ?? "")) : variable.valueControl === "duration" ? formatDuration(values.value) : String(values.value ?? "")
  const compactOperator = operator.key === "greater_than" ? ">" : operator.key === "less_than" ? "<" : operator.label.toLowerCase()
  return `${variable.label} ${compactOperator} ${value}`
}

function summarizeBlock(block: WorkflowBlockDefinition | undefined, config: Record<string, unknown>) {
  if (!block) return "Workflow block"

  const values = config ?? {}
  switch (block.type) {
    case "condition":
      return conditionSummary(values)
    case "schedule.trigger":
      return values["schedule_interval_minutes"] ? `Every ${formatDuration(values["schedule_interval_minutes"])}` : "Schedule trigger"
    case "delay":
      return values["minutes"] ? `Delay ${values["minutes"]} minutes` : "Delay"
    case "wait_until":
      return values["datetime"] ? String(values["datetime"]) : "Wait until"
    case "split_path":
      return Array.isArray(values["paths"]) ? `Split into ${values["paths"].length} paths` : "Split path"
    case "stop_workflow":
      return values["reason"] ? String(values["reason"]) : "Stop workflow"
    case "pipeline.move_stage":
      return values["to_stage_key"] ? `Move to ${humanizeKey(String(values["to_stage_key"]))}` : "Move stage"
    case "create_internal_note":
      return values["body"] ? "Add internal note" : "Create internal note"
    case "create_follow_up":
      return values["due_in_minutes"] ? `Create follow-up due in ${formatDuration(values["due_in_minutes"])}` : "Create follow-up"
    case "user.assign":
      return values["user_id"] ? `Assign user #${values["user_id"]}` : "Assign user"
    case "lead.update":
      return values["field"] ? `Update ${humanizeKey(String(values["field"]).split(".").pop() ?? "lead")}` : "Update lead"
    case "tag.add":
      return values["tag"] ? `Add tag ${values["tag"]}` : "Add tag"
    case "tag.remove":
      return values["tag"] ? `Remove tag ${values["tag"]}` : "Remove tag"
    case "task.create":
      return values["title"] ? `Create task: ${values["title"]}` : values["due_in_minutes"] ? `Create task due in ${formatDuration(values["due_in_minutes"])}` : "Create task"
    case "email.send":
      return values["template_key"] ? `Send email: ${humanizeKey(String(values["template_key"]))}` : "Send email"
    case "sms.send":
      return "SMS runtime not active yet"
    case "webhook.call":
      return "Webhook runtime not active yet"
    default:
      return block.display_name
  }
}
